<template>
    <Teleport :to="activeTarget" :disabled="!activeTarget">
        <div
            ref="wrapperRef"
            class="aurora-stage-wrapper"
            :class="{ 'is-active': !!activeTarget }"
            aria-hidden="true"
        ></div>
    </Teleport>
</template>

<script setup lang="ts">
    import { onMounted, onUnmounted, watch, useTemplateRef } from "vue";
    import { Renderer, Program, Mesh, Color, Triangle } from "ogl";
    import { AURORA_COLOR_STOPS } from "@/components/animated/auroraPalette";
    import { useAuroraStage } from "@/composables/useAuroraStage";

    const { activeTarget } = useAuroraStage();
    const wrapperRef = useTemplateRef<HTMLDivElement>("wrapperRef");

    const VERT = `#version 300 es
  in vec2 position;
  void main() {
    gl_Position = vec4(position, 0.0, 1.0);
  }
  `;

    const FRAG = `#version 300 es
  precision highp float;

  uniform float uTime;
  uniform float uAmplitude;
  uniform vec3 uColorStops[3];
  uniform vec2 uResolution;
  uniform float uBlend;
  uniform float uIntensity;
  uniform float uNoiseIntensity;

  out vec4 fragColor;

  vec3 permute(vec3 x) {
    return mod(((x * 34.0) + 1.0) * x, 289.0);
  }

  float snoise(vec2 v){
    const vec4 C = vec4(
        0.211324865405187, 0.366025403784439,
        -0.577350269189626, 0.024390243902439
    );
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod(i, 289.0);

    vec3 p = permute(
        permute(i.y + vec3(0.0, i1.y, 1.0))
      + i.x + vec3(0.0, i1.x, 1.0)
    );

    vec3 m = max(
        0.5 - vec3(
            dot(x0, x0),
            dot(x12.xy, x12.xy),
            dot(x12.zw, x12.zw)
        ),
        0.0
    );
    m = m * m;
    m = m * m;

    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);

    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }

  struct ColorStop {
    vec3 color;
    float position;
  };

  #define COLOR_RAMP(colors, factor, finalColor) {              \
    int index = 0;                                            \
    for (int i = 0; i < 2; i++) {                               \
       ColorStop currentColor = colors[i];                    \
       bool isInBetween = currentColor.position <= factor;    \
       index = int(mix(float(index), float(i), float(isInBetween))); \
    }                                                         \
    ColorStop currentColor = colors[index];                   \
    ColorStop nextColor = colors[index + 1];                  \
    float range = nextColor.position - currentColor.position; \
    float lerpFactor = (factor - currentColor.position) / range; \
    finalColor = mix(currentColor.color, nextColor.color, lerpFactor); \
  }

  float random(vec2 co) {
    return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
  }

  float noise(vec2 uv) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);

    float a = random(i);
    float b = random(i + vec2(1.0, 0.0));
    float c = random(i + vec2(0.0, 1.0));
    float d = random(i + vec2(1.0, 1.0));

    vec2 u = f * f * (3.0 - 2.0 * f);

    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / uResolution;

    ColorStop colors[3];
    colors[0] = ColorStop(uColorStops[0], 0.0);
    colors[1] = ColorStop(uColorStops[1], 0.5);
    colors[2] = ColorStop(uColorStops[2], 1.0);

    vec3 rampColor;
    COLOR_RAMP(colors, uv.x, rampColor);

    float height = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
    height = exp(height);
    height = (uv.y * 2.0 - height + 0.2);
    float intensity = 0.6 * height;

    float midPoint = 0.20;
    float auroraAlpha = smoothstep(midPoint - uBlend * 0.5, midPoint + uBlend * 0.5, intensity);

    vec3 auroraColor = rampColor;

    float finalAlpha = auroraAlpha * smoothstep(0.0, 0.5, intensity) * uIntensity;

    vec2 noiseUV = gl_FragCoord.xy / vec2(2.0) + uTime * 0.5;
    float noiseValue = noise(noiseUV);
    float grainAmount = (noiseValue - 0.5) * uNoiseIntensity;

    vec3 finalColor = auroraColor + vec3(grainAmount);

    fragColor = vec4(finalColor * finalAlpha, finalAlpha);
  }
  `;

    const RESOLUTION_SCALE = 0.5;
    const SPEED = 0.5;
    const AMPLITUDE = 0.3;
    const BLEND = 0.25;
    const INTENSITY = 1.0;
    const NOISE_INTENSITY = 0.025;

    let renderer: Renderer | null = null;
    let program: Program | null = null;
    let mesh: Mesh | null = null;
    let animateId = 0;
    let isDocumentVisible = true;
    let resizeObserver: ResizeObserver | null = null;
    let canvasEl: HTMLCanvasElement | null = null;
    let resolutionValue: [number, number] = [300, 300];

    const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const parseColorStops = (stops: readonly string[]): number[][] =>
        stops.map((hex) => {
            const c = new Color(hex);
            return [c.r, c.g, c.b];
        });

    const shouldRender = () =>
        isDocumentVisible &&
        !!activeTarget.value &&
        resolutionValue[0] > 0 &&
        resolutionValue[1] > 0;

    const scheduleFrame = () => {
        if (!shouldRender()) {
            animateId = 0;
            return;
        }
        animateId = requestAnimationFrame(update);
    };

    const update = (t: number) => {
        if (!program || !renderer || !mesh) {
            animateId = 0;
            return;
        }
        program.uniforms.uTime.value = t * 0.01 * SPEED * 0.1;
        renderer.render({ scene: mesh });
        scheduleFrame();
    };

    const resize = () => {
        const wrapper = wrapperRef.value;
        if (!wrapper || !renderer || !program) {
            return;
        }

        const rect = wrapper.getBoundingClientRect();
        const width = Math.max(Math.round(rect.width * RESOLUTION_SCALE), 1);
        const height = Math.max(Math.round(rect.height * RESOLUTION_SCALE), 1);

        if (width === resolutionValue[0] && height === resolutionValue[1]) {
            return;
        }

        renderer.setSize(width, height);
        // ogl's setSize writes the scaled px dimensions onto canvas.style; the
        // drawing buffer stays at half-res for perf but the canvas must still
        // stretch to fill its container, otherwise it sits in the top-left.
        if (canvasEl) {
            canvasEl.style.width = "100%";
            canvasEl.style.height = "100%";
        }
        resolutionValue = [width, height];
        program.uniforms.uResolution.value = resolutionValue;
    };

    const initGL = () => {
        const wrapper = wrapperRef.value;
        if (!wrapper || renderer) {
            return;
        }

        renderer = new Renderer({
            alpha: true,
            premultipliedAlpha: true,
            antialias: false,
            dpr: 1,
        });

        const gl = renderer.gl;
        gl.clearColor(0, 0, 0, 0);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.canvas.style.backgroundColor = "transparent";

        const geometry = new Triangle(gl);
        if (geometry.attributes.uv) {
            delete geometry.attributes.uv;
        }

        program = new Program(gl, {
            vertex: VERT,
            fragment: FRAG,
            uniforms: {
                uTime: { value: 0 },
                uAmplitude: { value: AMPLITUDE },
                uColorStops: { value: parseColorStops(AURORA_COLOR_STOPS) },
                uResolution: { value: resolutionValue },
                uBlend: { value: BLEND },
                uIntensity: { value: INTENSITY },
                uNoiseIntensity: { value: NOISE_INTENSITY },
            },
        });

        mesh = new Mesh(gl, { geometry, program });

        canvasEl = gl.canvas as HTMLCanvasElement;
        canvasEl.style.width = "100%";
        canvasEl.style.height = "100%";
        canvasEl.style.display = "block";
        canvasEl.style.position = "absolute";
        canvasEl.style.top = "0";
        canvasEl.style.left = "0";

        canvasEl.addEventListener("webglcontextlost", handleContextLost, false);
        canvasEl.addEventListener(
            "webglcontextrestored",
            handleContextRestored,
            false,
        );

        wrapper.appendChild(canvasEl);
    };

    const teardownGL = () => {
        if (animateId) {
            cancelAnimationFrame(animateId);
            animateId = 0;
        }
        if (canvasEl) {
            canvasEl.removeEventListener("webglcontextlost", handleContextLost);
            canvasEl.removeEventListener(
                "webglcontextrestored",
                handleContextRestored,
            );
            canvasEl.parentNode?.removeChild(canvasEl);
            canvasEl = null;
        }
        if (renderer) {
            renderer.gl.getExtension("WEBGL_lose_context")?.loseContext();
        }
        renderer = null;
        program = null;
        mesh = null;
    };

    const handleContextLost = (e: Event) => {
        e.preventDefault();
        if (animateId) {
            cancelAnimationFrame(animateId);
            animateId = 0;
        }
    };

    const handleContextRestored = () => {
        teardownGL();
        initGL();
        resize();
        if (!prefersReducedMotion) {
            scheduleFrame();
        } else if (renderer && mesh) {
            renderer.render({ scene: mesh });
        }
    };

    const onDocumentVisibility = () => {
        isDocumentVisible = !document.hidden;
        if (
            isDocumentVisible &&
            !animateId &&
            !prefersReducedMotion &&
            shouldRender()
        ) {
            scheduleFrame();
        }
    };

    watch(activeTarget, () => {
        // wait a tick for Teleport to move the wrapper into the new target
        requestAnimationFrame(() => {
            resize();
            if (!animateId && !prefersReducedMotion && shouldRender()) {
                scheduleFrame();
            } else if (prefersReducedMotion && renderer && mesh) {
                renderer.render({ scene: mesh });
            }
        });
    });

    onMounted(() => {
        initGL();
        resize();

        if (wrapperRef.value && "ResizeObserver" in window) {
            resizeObserver = new ResizeObserver(() => {
                resize();
                if (!animateId && !prefersReducedMotion && shouldRender()) {
                    scheduleFrame();
                }
            });
            resizeObserver.observe(wrapperRef.value);
        }

        document.addEventListener("visibilitychange", onDocumentVisibility);

        if (prefersReducedMotion && renderer && mesh) {
            renderer.render({ scene: mesh });
        } else if (shouldRender()) {
            scheduleFrame();
        }
    });

    onUnmounted(() => {
        document.removeEventListener("visibilitychange", onDocumentVisibility);
        resizeObserver?.disconnect();
        resizeObserver = null;
        teardownGL();
    });
</script>

<style scoped>
    .aurora-stage-wrapper {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        overflow: hidden;
    }

    .aurora-stage-wrapper:not(.is-active) {
        display: none;
    }
</style>
